import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { useTheme } from "@/hooks/useTheme";
import { Header } from "@/components/layout/Header";
import { ParameterPanel } from "@/components/parameters/ParameterPanel";
import { TopCombinations } from "@/components/sections/TopCombinations";
import { GlobalSearch } from "@/components/sections/GlobalSearch";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { Toaster } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";

export default function App() {
  const phase = useAppStore((s) => s.phase);
  const errorMessage = useAppStore((s) => s.errorMessage);
  const initialize = useAppStore((s) => s.initialize);
  useTheme();

  const [searchOpen, setSearchOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  // ⌘K / Ctrl+K opens search.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (phase === "loading") return <LoadingScreen />;
  if (phase === "error") return <ErrorScreen message={errorMessage} onRetry={initialize} />;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[1600px] flex-col p-2 sm:p-3">
      <Header onSearch={() => setSearchOpen(true)} onAdmin={() => setAdminOpen(true)} />

      {/* Fluid two-column layout: stacks on narrow extension zones, splits when
          there is room. No flex-1: the grid must hug its content, otherwise the
          rows stretch to the zone height and leave a large gap between the
          stacked panels. */}
      <main className="grid grid-cols-1 items-start gap-3 lg:grid-cols-12">
        {/* Section 1 — Parameter Controls. Height follows its content so the Go
            button sits directly beneath the parameters. */}
        <div className="lg:col-span-5">
          <ParameterPanel />
        </div>

        {/* Right column — Top Combinations */}
        <div className="flex flex-col gap-3 lg:col-span-7">
          <TopCombinations />
        </div>
      </main>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <AdminPanel open={adminOpen} onOpenChange={setAdminOpen} />
      <Toaster />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="mx-auto w-full max-w-[1600px] p-3 sm:p-4">
      <div className="mb-4 flex items-center gap-3 rounded-2xl glass-sm px-4 py-3">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-sm font-medium">Connecting to Tableau…</p>
      </div>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
        <Skeleton className="h-[420px] lg:col-span-5" />
        <div className="flex flex-col gap-4 lg:col-span-7">
          <Skeleton className="h-[260px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="glass max-w-md p-8 text-center">
        <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-warning" />
        <h2 className="text-lg font-semibold">Couldn’t initialize the extension</h2>
        <p className="mt-2 text-sm text-muted-foreground">{message ?? "An unexpected error occurred."}</p>
        <Button className="mt-5" onClick={onRetry}>Try again</Button>
      </div>
    </div>
  );
}
